import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import { useLanguage } from "../../contexts/LanguageContext";
import { RefreshCw, Sparkles, CheckCircle2, XCircle, Loader2, ShoppingCart, ExternalLink, AlertTriangle } from "lucide-react";

/**
 * V180 — Catalog Brain.
 *
 * For each connected store the user can:
 *   1. Pull products (catalog snapshot)
 *   2. Run AI SEO analysis → fix proposals
 *   3. Apply fixes (push back to platform)
 *
 * Top section: store picker + summary cards.
 * Bottom section: fix proposals table with per-row + bulk Apply.
 */
export default function CatalogBrainTab({ user }) {
  const { t, language } = useLanguage();
  const [stores, setStores] = useState([]);
  const [activeStoreId, setActiveStoreId] = useState("");
  const [summary, setSummary] = useState(null);
  const [fixes, setFixes] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState("");
  const [selected, setSelected] = useState({});
  const [toast, setToast] = useState(null);

  const flash = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4500);
  };

  // Load stores
  useEffect(() => {
    if (!user?.email) return;
    axios.get(`${API}/connected-stores`, { params: { email: user.email } })
      .then(r => {
        const list = r.data?.stores || [];
        setStores(list);
        if (list.length && !activeStoreId) setActiveStoreId(list[0].store_id);
      })
      .catch(() => setStores([]));
  }, [user?.email]);  // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(async () => {
    if (!activeStoreId) return;
    try {
      const [sumRes, fixRes] = await Promise.all([
        axios.get(`${API}/catalog/summary`, { params: { store_id: activeStoreId } }),
        axios.get(`${API}/catalog/fixes`, { params: { store_id: activeStoreId, status: filter } }),
      ]);
      setSummary(sumRes.data);
      setFixes(fixRes.data?.fixes || []);
      setSelected({});
    } catch (e) {
      flash("error", e?.response?.data?.detail || t.cat_t_load_fail);
    }
  }, [activeStoreId, filter, t]);

  useEffect(() => { refresh(); }, [refresh]);

  const pullCatalog = async () => {
    setBusy("pull");
    try {
      const r = await axios.post(`${API}/catalog/pull/${activeStoreId}`, { limit: 100 });
      flash("ok", (t.cat_t_pulled || "Pulled {n} products").replace("{n}", r.data?.pulled || 0));
      await refresh();
    } catch (e) {
      flash("error", e?.response?.data?.detail || t.cat_t_pull_fail);
    } finally { setBusy(""); }
  };

  const analyse = async () => {
    setBusy("analyse");
    try {
      const r = await axios.post(`${API}/catalog/analyse/${activeStoreId}`,
        { max_products: 20, language });
      flash("ok", (t.cat_t_analyse || "")
        .replace("{ok}", r.data?.suggested || 0)
        .replace("{skipped}", r.data?.skipped_ok || 0)
        .replace("{failed}", r.data?.failed || 0));
      await refresh();
    } catch (e) {
      flash("error", e?.response?.data?.detail || t.cat_t_analyse_fail);
    } finally { setBusy(""); }
  };

  const applyOne = async (fix_id) => {
    setBusy(`apply_${fix_id}`);
    try {
      const r = await axios.post(`${API}/catalog/apply`, { fix_ids: [fix_id] });
      if (r.data?.applied > 0) flash("ok", t.cat_t_applied);
      else flash("error", r.data?.results?.[0]?.message || t.cat_t_apply_fail);
      await refresh();
    } catch (e) {
      flash("error", e?.response?.data?.detail || t.cat_t_apply_fail);
    } finally { setBusy(""); }
  };

  const applyAll = async () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (!ids.length) return flash("error", t.cat_t_select_some);
    setBusy("bulk");
    try {
      const r = await axios.post(`${API}/catalog/apply`, { fix_ids: ids });
      flash("ok", (t.cat_t_bulk || "")
        .replace("{ok}", r.data?.applied || 0)
        .replace("{failed}", r.data?.failed || 0));
      await refresh();
    } catch (e) {
      flash("error", e?.response?.data?.detail || t.cat_t_bulk_fail);
    } finally { setBusy(""); }
  };

  const skip = async (fix_id) => {
    try {
      await axios.post(`${API}/catalog/skip/${fix_id}`);
      await refresh();
    } catch {}
  };

  const toggleAll = (checked) => {
    const next = {};
    fixes.forEach(f => { next[f.fix_id] = checked; });
    setSelected(next);
  };

  const someSelected = Object.values(selected).some(Boolean);

  return (
    <div className="space-y-6" data-testid="catalog-brain-tab">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-950 via-teal-900 to-emerald-800 text-white p-6 lg:p-8 rounded-2xl shadow-xl border border-white/10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-emerald-300 font-extrabold mb-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> {t.cat_kicker} · V180
            </div>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tight">{t.cat_title}</h1>
            <p className="text-emerald-100/90 mt-2 text-sm max-w-2xl">
              {t.cat_lead}
            </p>
          </div>
          <div>
            <select
              value={activeStoreId}
              onChange={(e) => setActiveStoreId(e.target.value)}
              disabled={stores.length === 0}
              className="bg-black/30 border border-white/20 text-white rounded-lg px-3 py-2 text-sm font-mono backdrop-blur min-w-[220px]"
              data-testid="catalog-store-picker"
            >
              {stores.length === 0 && <option>{t.cat_no_stores_picker}</option>}
              {stores.map(s => (
                <option key={s.store_id} value={s.store_id} className="text-black">
                  {s.platform} · {s.store_url.replace(/^https?:\/\//, "").slice(0, 30)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {stores.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900" data-testid="catalog-no-stores">
          <AlertTriangle className="w-5 h-5 inline-block mr-2 -mt-1 text-amber-600" />
          {t.cat_no_stores_alert}
        </div>
      )}

      {/* Summary + actions */}
      {activeStoreId && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label={t.cat_stat_products} value={summary?.products ?? "—"} testid="cat-stat-products" />
            <StatCard label={t.cat_stat_pending} value={summary?.pending_fixes ?? "—"} testid="cat-stat-pending" color="text-orange-600" />
            <StatCard label={t.cat_stat_applied} value={summary?.applied_fixes ?? "—"} testid="cat-stat-applied" color="text-emerald-600" />
            <StatCard label={t.cat_stat_last_pull} value={summary?.last_pulled_at ? new Date(summary.last_pulled_at).toLocaleString(language || "en") : "—"} testid="cat-stat-last-pull" small />
          </div>

          <div className="flex flex-wrap gap-2">
            <Btn icon={RefreshCw} onClick={pullCatalog} busy={busy === "pull"} testid="catalog-pull-button">
              {t.cat_btn_pull}
            </Btn>
            <Btn icon={Sparkles} onClick={analyse} busy={busy === "analyse"} variant="primary" testid="catalog-analyse-button">
              {t.cat_btn_analyse}
            </Btn>
            <div className="flex items-center gap-1 ml-auto text-xs">
              <span className="text-gray-500 mr-1">{t.cat_filter_label}</span>
              {[
                { k: "pending", label: t.cat_filter_pending },
                { k: "applied", label: t.cat_filter_applied },
                { k: "skipped", label: t.cat_filter_skipped },
                { k: "failed", label: t.cat_filter_failed },
              ].map(({ k, label }) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setFilter(k)}
                  className={`px-3 py-1.5 rounded-full font-semibold ${filter === k ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                  data-testid={`catalog-filter-${k}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Toast */}
          {toast && (
            <div
              className={`text-sm rounded-lg p-3 border ${toast.kind === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-red-50 border-red-200 text-red-900"}`}
              data-testid="catalog-toast"
            >
              {toast.msg}
            </div>
          )}

          {/* Fix proposals */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 flex-wrap gap-2">
              <h2 className="text-sm font-bold text-gray-900">{t.cat_proposals} ({fixes.length})</h2>
              {filter === "pending" && fixes.length > 0 && (
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      onChange={(e) => toggleAll(e.target.checked)}
                      data-testid="catalog-select-all"
                    /> {t.cat_select_all}
                  </label>
                  <Btn icon={CheckCircle2} onClick={applyAll} busy={busy === "bulk"} disabled={!someSelected} variant="primary" testid="catalog-bulk-apply">
                    {t.cat_bulk_apply}
                  </Btn>
                </div>
              )}
            </div>
            {fixes.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-gray-500" data-testid="catalog-no-fixes">
                <ShoppingCart className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                {t.cat_no_fixes_pre} <strong>{filter}</strong>{t.cat_no_fixes_post}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {fixes.map(f => (
                  <FixRow
                    key={f.fix_id}
                    fix={f}
                    selected={!!selected[f.fix_id]}
                    onToggle={(v) => setSelected(prev => ({ ...prev, [f.fix_id]: v }))}
                    onApply={() => applyOne(f.fix_id)}
                    onSkip={() => skip(f.fix_id)}
                    busy={busy === `apply_${f.fix_id}`}
                    canApply={filter === "pending"}
                    t={t}
                  />
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, color = "text-gray-900", small = false, testid }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-testid={testid}>
      <div className="text-[10px] tracking-[0.18em] uppercase text-gray-500 font-bold">{label}</div>
      <div className={`${small ? "text-sm" : "text-2xl"} font-black ${color} mt-1.5 ${small ? "truncate" : ""}`}>{value}</div>
    </div>
  );
}

function Btn({ icon: Icon, children, onClick, busy, disabled, variant = "default", testid }) {
  const base = "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition-colors";
  const cls = variant === "primary"
    ? "bg-violet-600 text-white hover:bg-violet-700 disabled:bg-gray-300"
    : "bg-white border border-gray-300 text-gray-800 hover:bg-gray-50 disabled:opacity-50";
  return (
    <button type="button" onClick={onClick} disabled={busy || disabled} className={`${base} ${cls}`} data-testid={testid}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {children}
    </button>
  );
}

function FixRow({ fix, selected, onToggle, onApply, onSkip, busy, canApply, t }) {
  return (
    <li className="px-4 py-3 hover:bg-gray-50" data-testid={`fix-row-${fix.fix_id}`}>
      <div className="flex items-start gap-3">
        {canApply && (
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onToggle(e.target.checked)}
            className="mt-1"
            data-testid={`fix-select-${fix.fix_id}`}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-bold text-gray-900 truncate" data-testid={`fix-title-${fix.fix_id}`}>
              {fix.product_title || "(—)"}
            </p>
            {fix.product_url && (
              <a href={fix.product_url} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:text-violet-800">
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {fix.status === "applied" && <Tag color="emerald">{t.cat_tag_applied}</Tag>}
            {fix.status === "failed" && <Tag color="red">{t.cat_tag_failed}</Tag>}
            {fix.status === "skipped" && <Tag color="gray">{t.cat_tag_skipped}</Tag>}
          </div>

          <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-3 text-xs">
            <DiffCell label={t.cat_diff_title} current={fix.current_seo_title} proposed={fix.proposed_seo_title} t={t} />
            <DiffCell label={t.cat_diff_meta} current={fix.current_seo_desc} proposed={fix.proposed_seo_desc} t={t} />
            <DiffCell label={t.cat_diff_alt} current={fix.current_image_alt} proposed={fix.proposed_image_alt} t={t} />
            <div className="bg-amber-50 border border-amber-200 rounded p-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">{t.cat_why}</div>
              <div className="text-amber-900 mt-1">{fix.rationale || "—"}</div>
              {Array.isArray(fix.reasons) && fix.reasons.length > 0 && (
                <div className="text-[10px] text-amber-700/80 mt-1">{fix.reasons.join(" · ")}</div>
              )}
            </div>
          </div>

          {fix.push_error && (
            <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid={`fix-error-${fix.fix_id}`}>
              ⚠ {fix.push_error}
            </div>
          )}

          {canApply && (
            <div className="mt-3 flex gap-2">
              <Btn icon={CheckCircle2} onClick={onApply} busy={busy} variant="primary" testid={`fix-apply-${fix.fix_id}`}>
                {t.cat_apply}
              </Btn>
              <Btn icon={XCircle} onClick={onSkip} testid={`fix-skip-${fix.fix_id}`}>
                {t.cat_skip}
              </Btn>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function DiffCell({ label, current, proposed, t }) {
  return (
    <div className="rounded border border-gray-200 bg-white">
      <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-500 border-b border-gray-200 bg-gray-50">
        {label}
      </div>
      <div className="p-2 space-y-1.5">
        <div>
          <div className="text-[10px] text-gray-500 font-bold">{t.cat_diff_current}</div>
          <div className="text-gray-700 line-clamp-3 break-words">{current || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] text-emerald-600 font-bold">{t.cat_diff_new}</div>
          <div className="text-emerald-900 font-semibold line-clamp-3 break-words">{proposed || "—"}</div>
        </div>
      </div>
    </div>
  );
}

function Tag({ color, children }) {
  const map = {
    emerald: "bg-emerald-100 text-emerald-700",
    red: "bg-red-100 text-red-700",
    gray: "bg-gray-100 text-gray-700",
  };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${map[color] || map.gray}`}>{children}</span>;
}
